import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import api from "../../constants/api";
import { showError, showSuccess } from "../../utils/toast.js";
import { emitEntityAccessDenied, isNotAssignedError } from "../../utils/apiErrorCodes.js";
import { useTheme } from "../../context/ThemeContext.jsx";
import { useAuth } from "../ui/AuthContext.jsx";
import { useConfirm } from "../ui/ConfirmationModal.jsx";
import { canEnterHoursUnlimited } from "../../constants/roles.js";
import CollapsibleBlock from "../ui/CollapsibleBlock";
import BlockSectionToolbar from "../ui/BlockSectionToolbar.jsx";
import BlockSectionSearch from "../ui/BlockSectionSearch.jsx";
import { Z_INDEX } from "../ui/zIndex.js";
import FilePreviewModal from "../ui/FilePreviewModal.jsx";
import FileActions from "../ui/FileActions.jsx";
import { getAllowedExtensionsForAccept } from "../../constants/fileExtensions";
import {
  getFileExtension,
  sanitizeFileUrl,
  shouldUsePreviewMeta,
} from "../../utils/filePreview.js";
import { resolveEntityFileUrl } from "../../utils/entityFileUrl.js";
import AppButton from "../ui/AppButton.jsx";
import useIsMobile from "../ui/useIsMobile.js";
import { Input, Select as NativeSelect } from "../forms/shared/FormAtoms.jsx";
import FilePickButton from "../forms/shared/FilePickButton.jsx";
import { successButtonLoadingClassName } from "../../design/tokens.js";
const MAX_FILE_BYTES = 50 * 1024 * 1024;
const uniqSort = (arr = []) => Array.from(new Set(arr.filter(Boolean))).sort((a, b) => a.localeCompare(b, "de"));
const calcRect = el => {
  if (!el) return {
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    openUp: false
  };
  const r = el.getBoundingClientRect();
  const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
  const spaceBelow = Math.max(0, viewportH - (r.top + r.height));
  const spaceAbove = r.top;
  return {
    left: r.left,
    top: r.top,
    width: r.width,
    height: r.height,
    openUp: spaceAbove > spaceBelow
  };
};
const formatSize = (bytes = 0) => bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, (bytes / 1024).toFixed(0))} KB`;
function defaultParseItems(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.dateien || [];
}
export function createEntityFilesBlock(config) {
  const {
    title = "📁 Dokumente",
    defaultTypes = ["Plan", "Offerte", "Rechnung", "Bestellung", "Protokoll", "Rapport", "E-Mail", "Foto", "Skizze", "SINA", "Mängelbericht", "Behebungsanzeige", "Messprotokoll", "Sonstiges"],
    getApiBase,
    getApiPaths = null,
    getTypeEndpoint = null,
    parseItems = defaultParseItems,
    /** @type {boolean} Nur bewusst true: erlaubt public_url (ohne /uploads). Nie dateipfad/pfad. */
    allowUnsafeLegacyPublicUrl = false,
    resolveFileUrl = null,
    uploadGuardMessage = null,
    canUpload = () => true,
    fileActionMode = "minimal",
    fileActionCompact = false,
    compact = false,
    /** resolveFileAccess-Modul für preview-meta, z. B. customer_files */
    previewModule = "",
  } = config;
  const contentPad = compact ? "px-3 sm:px-4 pb-3 sm:pb-4" : "px-4 sm:px-6 pb-4 sm:pb-5";
  return function EntityFilesBlock({
    entityId
  }) {
    const {
      user
    } = useAuth();
    const confirm = useConfirm();
    const isMobile = useIsMobile();
    const deleteByRole = canEnterHoursUnlimited(user);
    const apiBase = useMemo(() => getApiBase ? getApiBase(entityId) : null, [entityId]);
    const apiPaths = useMemo(() => {
      if (getApiPaths) return getApiPaths(entityId);
      return {
        list: apiBase ? `${apiBase}/dateien` : null,
        create: apiBase ? `${apiBase}/upload` : null,
        remove: id => apiBase ? `${apiBase}/dateien/${id}` : null
      };
    }, [entityId, apiBase]);
    const mayUpload = useMemo(() => canUpload(entityId, user), [entityId, user]);
    const resolveRowFileUrl = useCallback(
      (row) => {
        if (typeof resolveFileUrl === "function") return resolveFileUrl(row);
        return resolveEntityFileUrl(row, { allowUnsafeLegacyPublicUrl });
      },
      [allowUnsafeLegacyPublicUrl, resolveFileUrl]
    );
    const [items, setItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [type, setType] = useState("");
    const [beschreibung, setBeschreibung] = useState("");
    const [suche, setSuche] = useState("");
    const [q, setQ] = useState("");
    const [types, setTypes] = useState(defaultTypes);
    const [comboOpen, setComboOpen] = useState(false);
    const [comboHi, setComboHi] = useState(-1);
    const [menuPos, setMenuPos] = useState({
      left: 0,
      top: 0,
      width: 0,
      height: 0,
      openUp: false
    });
    const [filePreviewOpen, setFilePreviewOpen] = useState(false);
    const [filePreviewUrl, setFilePreviewUrl] = useState("");
    const [filePreviewName, setFilePreviewName] = useState("");
    const [filePreviewType, setFilePreviewType] = useState("");
    const [filePreviewId, setFilePreviewId] = useState(null);
    const [filePreviewProjektTyp, setFilePreviewProjektTyp] = useState("");
    const [filePreviewProjektId, setFilePreviewProjektId] = useState(null);
    const [filePreviewEntityId, setFilePreviewEntityId] = useState(null);
    const mountedRef = useRef(true);
    const dropRef = useRef(null);
    const comboRef = useRef(null);
    const menuRef = useRef(null);
    const inputRef = useRef(null);
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);
    useEffect(() => {
      const t = setTimeout(() => setQ((suche || "").trim().toLowerCase()), 150);
      return () => clearTimeout(t);
    }, [suche]);
    useEffect(() => {
      if (!getTypeEndpoint) return;
      const endpoint = getTypeEndpoint(entityId);
      if (!endpoint) return;
      let active = true;
      (async () => {
        try {
          const res = await api.get(endpoint);
          const remote = Array.isArray(res.data?.items) ? res.data.items : [];
          if (active) setTypes(uniqSort([...defaultTypes, ...remote]));
        } catch {
          // keep defaults
        }
      })();
      return () => {
        active = false;
      };
    }, [entityId]);
    const loadItems = useCallback(async () => {
      if (!apiPaths?.list) return;
      try {
        setListLoading(true);
        const res = await api.get(apiPaths.list, { silent: true });
        const rows = parseItems(res.data);
        setItems(Array.isArray(rows) ? rows : []);
      } catch (err) {
        console.error("Dateien laden fehlgeschlagen:", err);
        setItems([]);
        if (isNotAssignedError(err)) {
          const detail =
            entityId && typeof entityId === "object" && entityId.projektId != null
              ? { entityType: "projekt", entityId: Number(entityId.projektId), projektTyp: entityId.projektTyp }
              : String(apiPaths?.list || "").includes("/service/")
                ? { entityType: "service", entityId: Number(entityId) }
                : null;
          if (detail) emitEntityAccessDenied(detail);
        } else {
          showError("Dateien konnten nicht geladen werden");
        }
      } finally {
        if (mountedRef.current) setListLoading(false);
      }
    }, [apiPaths, parseItems]);
    useEffect(() => {
      loadItems();
    }, [loadItems]);
    const typeOptions = useMemo(() => {
      const base = uniqSort(types);
      const current = String(type || "").trim();
      if (current && !base.includes(current)) return uniqSort([...base, current]);
      return base;
    }, [types, type]);

    useEffect(() => {
      if (isMobile) setComboOpen(false);
    }, [isMobile]);

    useEffect(() => {
      if (!comboOpen || isMobile) return;
      const onDown = e => {
        const inField = comboRef.current && comboRef.current.contains(e.target);
        const inMenu = menuRef.current && menuRef.current.contains(e.target);
        if (!inField && !inMenu) {
          setComboOpen(false);
          setComboHi(-1);
        }
      };
      const update = () => setMenuPos(calcRect(comboRef.current));
      update();
      document.addEventListener("mousedown", onDown);
      document.addEventListener("focusin", onDown);
      window.addEventListener("scroll", update, true);
      window.addEventListener("resize", update);
      return () => {
        document.removeEventListener("mousedown", onDown);
        document.removeEventListener("focusin", onDown);
        window.removeEventListener("scroll", update, true);
        window.removeEventListener("resize", update);
      };
    }, [comboOpen, isMobile]);
    const validateFile = useCallback(f => {
      if (!f) return "Bitte eine Datei auswählen";
      if (f.size > MAX_FILE_BYTES) return `Datei zu gross (${formatSize(f.size)}). Max ${formatSize(MAX_FILE_BYTES)}.`;
      const accept = getAllowedExtensionsForAccept().split(",").map(s => s.trim());
      const ext = `.${getFileExtension(f.name)}`;
      if (!accept.includes(ext)) return `Dateityp nicht erlaubt (${ext}).`;
      return null;
    }, []);
    useEffect(() => {
      if (!formOpen || !mayUpload) return;
      const el = dropRef.current;
      if (!el) return;
      const stop = e => {
        e.preventDefault();
        e.stopPropagation();
      };
      const over = e => {
        stop(e);
        el.classList.add("ring", "ring-blue-400/40");
      };
      const leave = e => {
        stop(e);
        el.classList.remove("ring", "ring-blue-400/40");
      };
      const onDrop = e => {
        stop(e);
        el.classList.remove("ring", "ring-blue-400/40");
        const f = e.dataTransfer?.files?.[0];
        if (!f) return;
        const err = validateFile(f);
        if (err) {
          showError(err);
          return;
        }
        setFile(f);
        if (inputRef.current) inputRef.current.value = "";
      };
      el.addEventListener("dragover", over);
      el.addEventListener("dragleave", leave);
      el.addEventListener("drop", onDrop);
      return () => {
        el.classList.remove("ring", "ring-blue-400/40");
        el.removeEventListener("dragover", over);
        el.removeEventListener("dragleave", leave);
        el.removeEventListener("drop", onDrop);
      };
    }, [formOpen, mayUpload, validateFile]);
    const handleUpload = async e => {
      e.preventDefault();
      if (!apiPaths?.create) {
        if (uploadGuardMessage) showError(uploadGuardMessage);
        return;
      }
      if (!mayUpload) return;
      if (!type.trim()) return showError("Bitte einen Typ wählen");
      const err = validateFile(file);
      if (err) return showError(err);
      const fd = new FormData();
      fd.append("file", file);
      fd.append("typ", type.trim());
      fd.append("beschreibung", String(beschreibung || ""));
      try {
        setUploading(true);
        await api.post(apiPaths.create, fd);
        setFile(null);
        setType("");
        setBeschreibung("");
        setFormOpen(false);
        if (inputRef.current) inputRef.current.value = "";
        await loadItems();
        showSuccess("Datei hochgeladen ✅");
      } catch (errUpload) {
        console.error("Upload fehlgeschlagen:", errUpload);
        showError(errUpload?.response?.data?.error || errUpload?.message || "Fehler beim Hochladen");
      } finally {
        setUploading(false);
      }
    };
    const handleDelete = async (id, originalname) => {
      const {
        ok
      } = await confirm({
        title: "Datei löschen",
        message: `Datei "${originalname || id}" wirklich löschen?`,
        variant: "danger",
        confirmLabel: "Löschen"
      });
      if (!ok) return;
      try {
        const url = apiPaths?.remove?.(id);
        if (!url) return;
        await api.delete(url);
        await loadItems();
        showSuccess("In Papierkorb verschoben");
      } catch (err) {
        console.error("Datei löschen fehlgeschlagen:", err);
        showError(err, {
          fallbackKey: "Fehler beim Löschen"
        });
      }
    };
    const filtered = useMemo(() => {
      if (!q) return items;
      return (items || []).filter(row => [row?.typ, row?.beschreibung, row?.originalname].filter(Boolean).some(s => String(s).toLowerCase().includes(q)));
    }, [items, q]);
    const fileCount = items.length;
    const info = `${fileCount} Datei${fileCount === 1 ? "" : "en"}`;
    const list = uniqSort(type ? [...types.filter(t => t.toLowerCase().includes(type.toLowerCase()))] : types);
    const sucheTrim = String(suche || "").trim();
    return (
      <CollapsibleBlock
        compact={compact}
        title={title}
        headerInfo={info}
        headerInfoMobile={String(fileCount)}
        headerInfoTitle={info}
        contentClassName={contentPad}
        useChevronUp
        showBorderBottom
      >
        <BlockSectionToolbar
          search={
            <BlockSectionSearch
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              onClear={() => setSuche("")}
              placeholder="Suche…"
              title="Typ, Beschreibung oder Dateiname …"
              ariaLabel="Dateien durchsuchen"
              wrapperClassName="relative w-full min-w-0 flex-1 xl:min-w-[20rem]"
            />
          }
          action={
            <AppButton
              type="button"
              onClick={() => {
                if (!apiPaths?.create && uploadGuardMessage) {
                  showError(uploadGuardMessage);
                  return;
                }
                if (!mayUpload) return;
                setFormOpen(v => !v);
              }}
              disabled={!mayUpload}
              className="px-4 py-2 rounded text-sm shrink-0"
              variant="secondary"
              size="sm"
            >
              {formOpen ? "Abbrechen" : "Datei hinzufügen"}
            </AppButton>
          }
        />

        {formOpen && mayUpload ? <form onSubmit={handleUpload} ref={dropRef} className={"border border-dashed border-white/15 p-4 rounded mb-4 bg-white/5"} aria-label="Datei-Upload">
            <div className={`flex flex-col items-center justify-center text-sm py-2 ${"text-blue-100/80"}`}>
              <FilePickButton
                ref={inputRef}
                id={`${title}-upload-input`}
                accept={getAllowedExtensionsForAccept()}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                disabled={uploading}
              />
              <p className="mt-1 text-[11px] text-center text-blue-200/55 max-md:hidden">Oder Drag-and-Drop</p>
              {file ? <p className="mt-2 text-xs">✅ <span className="font-medium">{file.name}</span> <span className={"text-blue-200/70"}>({formatSize(file.size)})</span></p> : null}
            </div>
            <div className="flex flex-col md:flex-row gap-2 mb-2">
              <div className="w-full md:w-auto min-w-0">
                {isMobile ? (
                  <NativeSelect
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    required
                    disabled={uploading}
                    className="w-full"
                    aria-label="Dokumenttyp"
                  >
                    <option value="">Typ wählen…</option>
                    {typeOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </NativeSelect>
                ) : (
                  <div ref={comboRef} className="relative w-full">
                    <Input
                      type="text"
                      placeholder="Typ wählen oder tippen…"
                      value={type}
                      onFocus={() => {
                        setComboOpen(true);
                        setMenuPos(calcRect(comboRef.current));
                      }}
                      onChange={(e) => {
                        setType(e.target.value);
                        setComboOpen(true);
                        setMenuPos(calcRect(comboRef.current));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "ArrowDown") {
                          e.preventDefault();
                          setComboOpen(true);
                          setComboHi((h) => Math.min(h + 1, list.length - 1));
                        }
                        if (e.key === "ArrowUp") {
                          e.preventDefault();
                          setComboOpen(true);
                          setComboHi((h) => Math.max(h - 1, 0));
                        }
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (comboHi >= 0 && list[comboHi]) setType(list[comboHi]);
                          setComboOpen(false);
                          setComboHi(-1);
                        }
                        if (e.key === "Escape") {
                          setComboOpen(false);
                          setComboHi(-1);
                        }
                      }}
                      className="w-full bg-[#0b1324] text-blue-100 border border-white/10 rounded px-3 py-2 text-sm pr-9"
                      required
                    />
                    <AppButton
                      type="button"
                      onClick={() => setComboOpen((v) => !v)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-blue-200/70"
                      variant="ghost"
                      size="sm"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </AppButton>
                    {comboOpen
                      ? createPortal(
                          <div
                            ref={menuRef}
                            style={{
                              position: "fixed",
                              left: menuPos.left,
                              top: menuPos.openUp
                                ? menuPos.top - 6
                                : menuPos.top + menuPos.height + 6,
                              width: menuPos.width || 260,
                              zIndex: Z_INDEX.dropdown,
                              transform: menuPos.openUp ? "translateY(-100%)" : "none",
                            }}
                          >
                            <div className="max-h-72 overflow-auto rounded-2xl border p-1 border-white/10 bg-[#0b1324]">
                              {list.map((opt, idx) => (
                                <div
                                  key={opt}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setType(opt);
                                    setComboOpen(false);
                                    setComboHi(-1);
                                  }}
                                  onMouseEnter={() => setComboHi(idx)}
                                  className={`cursor-pointer rounded px-3 py-2 text-sm ${
                                    idx === comboHi
                                      ? "bg-white/10 text-blue-100"
                                      : "text-blue-100/90"
                                  }`}
                                >
                                  {opt}
                                </div>
                              ))}
                            </div>
                          </div>,
                          document.body,
                        )
                      : null}
                  </div>
                )}
              </div>
              <Input type="text" placeholder="Beschreibung (optional)" value={beschreibung} onChange={e => setBeschreibung(e.target.value)} className={"bg-[#0b1324] text-blue-100 border border-white/10 px-3 py-2 rounded text-sm w-full"} />
              <AppButton type="submit" disabled={uploading} variant="success" size="sm" className={uploading ? successButtonLoadingClassName : ""}>
                {uploading ? "Lädt…" : "Speichern"}
              </AppButton>
            </div>
          </form> : null}

        {listLoading ? <div className="mt-2 space-y-2 animate-pulse">
            <div className={`h-12 rounded ${"bg-white/10"}`} />
            <div className={`h-12 rounded ${"bg-white/10"}`} />
          </div> : filtered.length === 0 ? <p className={"text-blue-200/70 mt-2 text-sm"}>Keine Dateien vorhanden.</p> : <div className="mt-2 space-y-2 max-h-[60vh] overflow-y-auto pr-1">
            {filtered.map(d => {
          const ext = getFileExtension(d?.originalname || d?.dateipfad);
          const canDelete = d?.loeschbar === true || deleteByRole;
          const rawUrl = resolveRowFileUrl(d);
          const url = sanitizeFileUrl(rawUrl);
          const fileId = d.id ?? null;
          const useMetaPreview = shouldUsePreviewMeta({ previewModule, fileId });
          const showPreview = Boolean(url || useMetaPreview);
          return <div key={d.id} className={"flex items-start justify-between gap-2 border border-white/10 px-4 py-3 rounded bg-white/5 min-w-0"}>
                  <div className="min-w-0 flex-1">
                    <div className={`font-semibold text-sm mb-0.5 break-words line-clamp-2 ${"text-blue-100/90"}`} title={`${d.typ || "Dokument"} - ${d.beschreibung || "Keine Beschreibung"}`}>
                      {d.typ || "Dokument"} - {d.beschreibung || "Keine Beschreibung"}
                    </div>
                    <div className={`text-xs italic break-all line-clamp-2 ${"text-blue-200/70"}`} title={d.originalname || ""}>{d.originalname}</div>
                  </div>
                  <FileActions
                    url={url}
                    fileName={d.originalname || "datei"}
                    showPreview={showPreview}
                    onPreview={() => {
                      setFilePreviewUrl(url || sanitizeFileUrl(rawUrl));
                      setFilePreviewName(d.originalname || "datei");
                      setFilePreviewType(ext);
                      setFilePreviewId(fileId);
                      if (previewModule === "project_files" && entityId && typeof entityId === "object") {
                        setFilePreviewProjektTyp(entityId.projektTyp || "");
                        setFilePreviewProjektId(entityId.projektId ?? null);
                        setFilePreviewEntityId(null);
                      } else if (previewModule === "profile_training_files") {
                        setFilePreviewEntityId(entityId ?? null);
                        setFilePreviewProjektTyp("");
                        setFilePreviewProjektId(null);
                      } else {
                        setFilePreviewProjektTyp("");
                        setFilePreviewProjektId(null);
                        setFilePreviewEntityId(null);
                      }
                      setFilePreviewOpen(true);
                    }}
                    canDelete={canDelete}
                    onDelete={() => handleDelete(d.id, d.originalname)}
                    mode={fileActionMode}
                    compact={fileActionCompact}
                  />
                </div>;
        })}
          </div>}

        <FilePreviewModal
          open={filePreviewOpen}
          onClose={() => setFilePreviewOpen(false)}
          fileUrl={filePreviewUrl}
          fileName={filePreviewName}
          fileType={filePreviewType}
          previewModule={previewModule}
          fileId={filePreviewId}
          projektTyp={filePreviewProjektTyp}
          projektId={filePreviewProjektId}
          entityId={filePreviewEntityId}
        />
      </CollapsibleBlock>
    );
  };
}